import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSessionContext } from '@supabase/auth-helpers-react';
import { LogOut, RefreshCw } from 'lucide-react';


import { useUserInfo } from '../hooks/useUserInfo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { formatDate } from '../lib/utils';
import { supabase } from '@/lib/supabase';
import { useGemBalance } from '@/hooks/useGemBalance';
import { isTwaSession } from '@/lib/detection';

export default function SettingsPage() {
  const { session, isLoading: isSessionLoading } = useSessionContext();
  const navigate = useNavigate();
  const { 
    userDetails,
    subscriptions, 
    isLoading, 
    updateUserDetails,
    isSubscriber
  } = useUserInfo();
  const { balance } = useGemBalance();

  // Form state for personal info
  const [nickname, setNickname] = useState('');
  const [gender, setGender] = useState('');
  const [age, setAge] = useState<number | undefined>();
  const [ageError, setAgeError] = useState<string | null>(null);
  const [aboutMe, setAboutMe] = useState('');
  const [personalInfoModified, setPersonalInfoModified] = useState(false);
  const [isSavingPersonalInfo, setIsSavingPersonalInfo] = useState(false);
  
  // Form state for preferences
  // const [language, setLanguage] = useState('en');
  const [emailOptIn, setEmailOptIn] = useState(true);
  const [preferencesModified, setPreferencesModified] = useState(false);
  const [isSavingPreferences, setIsSavingPreferences] = useState(false);

  // Initialize form with user data
  useEffect(() => {
    if (userDetails) {
      setNickname(userDetails.usr_nickname || '');
      setGender(userDetails.usr_gender || '');
      setAge(userDetails.usr_age || undefined);
      setAboutMe(userDetails.usr_aboutme || '');
      
      // Get meta data if available
      if (userDetails.eh_usr_meta) {
        try {
          const meta = typeof userDetails.eh_usr_meta === 'string' 
            ? JSON.parse(userDetails.eh_usr_meta)
            : userDetails.eh_usr_meta;
          
          // setLanguage(meta.usr_language || 'en');
          setEmailOptIn(meta.email_optin !== false); // Default to true if not set
        } catch (error) {
          console.error('Failed to parse user meta data', error);
        }
      }
    }
  }, [userDetails]);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isSessionLoading && !session) {
      navigate('/');
    }
  }, [isSessionLoading, session, navigate]);

  // Validate age function
  const validateAge = (value: number | undefined): string | null => {
    if (value !== undefined) {
      if (value < 18) {
        return 'You must be at least 18 years old to use EveningHoney.';
      } else if (value > 118) {
        return 'Age must be 118 or less.';
      }
    }
    return null;
  };

  // Handle save personal info
  const handleSavePersonalInfo = async () => {
    // Validate before saving
    const ageValidationError = validateAge(age);
    if (ageValidationError) {
      setAgeError(ageValidationError);
      return;
    }
    
    setIsSavingPersonalInfo(true);
    
    try {
      await updateUserDetails({
        usr_nickname: nickname,
        usr_gender: gender,
        usr_age: age,
        usr_aboutme: aboutMe
      });
      
      setPersonalInfoModified(false);
    } catch (error) {
      console.error('Failed to save personal information', error);
    } finally {
      setIsSavingPersonalInfo(false);
    }
  };

  // Handle save preferences
  const handleSavePreferences = async () => {
    setIsSavingPreferences(true);
    
    try {
      // Prepare meta data
      const metaData = {
        ...(typeof userDetails?.eh_usr_meta === 'object' ? userDetails.eh_usr_meta : {}),
        // usr_language: language,
        email_optin: emailOptIn
      };
      
      await updateUserDetails({
        eh_usr_meta: metaData
      });
      
      setPreferencesModified(false);
    } catch (error) {
      console.error('Failed to save preferences', error);
    } finally {
      setIsSavingPreferences(false);
    }
  };

  // Handle cancel personal info
  const handleCancelPersonalInfo = () => {
    // Reset form to current values
    if (userDetails) {
      setNickname(userDetails.usr_nickname || '');
      setGender(userDetails.usr_gender || '');
      setAge(userDetails.usr_age || undefined);
      setAgeError(userDetails.usr_age ? validateAge(userDetails.usr_age) : null);
      setAboutMe(userDetails.usr_aboutme || '');
    }
    setPersonalInfoModified(false);
  };

  // Handle cancel preferences
  const handleCancelPreferences = () => {
    // Reset form to current values
    if (userDetails && userDetails.eh_usr_meta) {
      try {
        const meta = typeof userDetails.eh_usr_meta === 'string'
          ? JSON.parse(userDetails.eh_usr_meta)
          : userDetails.eh_usr_meta;
        
        // setLanguage(meta.usr_language || 'en');
        setEmailOptIn(meta.email_optin !== false);
      } catch (error) {
        console.error('Failed to parse user meta data', error);
      }
    }
    
    setPreferencesModified(false);
  };

  // Track form changes for personal info
  const handlePersonalInfoChange = () => {
    setPersonalInfoModified(true);
  };

  // Track form changes for preferences
  const handlePreferencesChange = () => {
    setPreferencesModified(true);
  };

  // Get active subscription information
  const activeSubscription = subscriptions.find(
    sub => sub.status === 'active' && sub.end_at && new Date(sub.end_at) > new Date()
  );

  // Check if there are any validation errors for personal info
  const hasPersonalInfoErrors = ageError !== null;

  // Handle age blur event
  const handleAgeBlur = () => {
    const error = validateAge(age);
    setAgeError(error);
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
  };

  if (isLoading || isSessionLoading) {
    return (
      <div className="flex-auto bg-zinc-800 text-white">
        <div className="container mx-auto py-8 px-4">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-2xl font-bold text-white mb-6">Profile Settings</h1>
            <div className="bg-zinc-900 rounded-lg p-6 animate-pulse">
              <div className="h-8 bg-zinc-800 rounded-md mb-4"></div>
              <div className="h-8 bg-zinc-800 rounded-md mb-4"></div>
              <div className="h-8 bg-zinc-800 rounded-md mb-4"></div>
              <div className="h-20 bg-zinc-800 rounded-md"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-auto bg-zinc-800 text-white">
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="text-2xl font-bold text-white mb-6">Account Settings</h1>
          
          {/* Account Settings */}
          <div className="bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Personal Information</h2>
            
            <div className="space-y-4">
              {/* Email: TODO: allow editing */}
              <div>
                <Input 
                  id="email"
                  value={session?.user?.email}
                  className="bg-zinc-800 border-zinc-700 w-auto max-w-full field-sizing-content"
                  disabled
                />
              </div>

              <div>
                <label htmlFor="nickname" className="block text-sm font-medium mb-1">Nickname</label>
                <Input 
                  id="nickname"
                  value={nickname}
                  onChange={(e) => {
                    setNickname(e.target.value);
                    handlePersonalInfoChange();
                  }}
                  className="bg-zinc-800 border-zinc-700 w-auto max-w-full"
                />
              </div>
              
              <div>
                <label htmlFor="gender" className="block text-sm font-medium mb-1">Gender</label>
                <Select 
                  value={gender} 
                  onValueChange={(value) => {
                    setGender(value);
                    handlePersonalInfoChange();
                  }}
                >
                  <SelectTrigger id="gender" className="bg-zinc-800 border-zinc-700 w-32 max-w-full">
                    <SelectValue placeholder="Select gender" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                    <SelectItem value="unknown">Prefer not to say</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <label htmlFor="age" className="block text-sm font-medium mb-1">Age</label>
                <Input 
                  id="age"
                  type="number"
                  min={18}
                  max={118}
                  value={age !== undefined ? age : ''}
                  onChange={(e) => {
                    const value = e.target.value ? parseInt(e.target.value) : undefined;
                    setAge(value);
                    if (ageError) setAgeError(validateAge(value));
                    handlePersonalInfoChange();
                  }}
                  onBlur={handleAgeBlur}
                  className={`bg-zinc-800 border-zinc-700 w-32 max-w-full ${ageError ? 'border-red-500' : ''}`}
                />
                {ageError && (
                  <p className="text-red-500 text-xs mt-1">{ageError}</p>
                )}
              </div>
              
              <div>
                <label htmlFor="aboutMe" className="block text-sm font-medium mb-1">About Me</label>
                <textarea 
                  id="aboutMe"
                  value={aboutMe}
                  onChange={(e) => {
                    setAboutMe(e.target.value);
                    handlePersonalInfoChange();
                  }}
                  className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-md"
                  rows={4}
                />
              </div>

              {personalInfoModified && (
                <div className="flex justify-end gap-2 mt-4">
                  <Button 
                    onClick={handleCancelPersonalInfo}
                    disabled={isSavingPersonalInfo}
                    className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSavePersonalInfo}
                    disabled={isSavingPersonalInfo || hasPersonalInfoErrors}
                    className={`${hasPersonalInfoErrors ? 'bg-zinc-600 cursor-not-allowed' : 'bg-amber-600 hover:bg-amber-500'}`}
                  >
                    {isSavingPersonalInfo ? (
                      <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                    ) : 'Save Changes'}
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {/* Subscription */}
          {!isTwaSession() && (
          <div className="bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Current Plan</h2>
            <div className="flex items-center">
              <div className="flex-1">
                <p className="text-amber-500 font-medium">
                  <span className="font-bold">{isSubscriber ? 'Premium' : 'Free'}</span> 
                  <span>{activeSubscription?.status === 'canceled' && ' (Canceled)'}</span>
                </p>
                {activeSubscription?.end_at && (
                  <p className="text-zinc-400 text-sm">
                    Subscription ends {formatDate(activeSubscription.end_at)}
                  </p>
                )}
                {activeSubscription?.gems_allocated && (
                  <p className="text-zinc-400 text-sm">
                    <span>Monthly gems included: </span>
                    <span className="font-bold">{activeSubscription.gems_allocated}</span>
                  </p>
                )}
                {!!activeSubscription && (
                  <p className="text-zinc-400 text-sm">
                    <span>Monthly gems remaining: </span>
                    <span className="font-bold">{balance.monthly}</span>
                    {/* <br />
                    <span className="font-bold">Total gems: </span>
                    <span>{balance.total}</span> */}
                  </p>
                )}
                {balance.purchased > 0.3 && (
                  <p className="text-zinc-400 text-sm">
                    <span>Purchased gems remaining: </span>
                    <span className="font-bold">{balance.purchased}</span>
                  </p>
                )}
              </div>
              <Button asChild className="bg-amber-600 hover:bg-amber-500">
                <a href="/subscription">{isSubscriber ? 'Manage Subscription' : 'Upgrade Now'}</a>
              </Button>
            </div>
          </div>
          )}
          
          {/* Preferences */}
          <div className="bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Preferences</h2>
            
            <div className="space-y-6">
              {/* <div>
                <label htmlFor="language" className="block text-sm font-medium mb-1">Language</label>
                <Select 
                  value={language} 
                  onValueChange={(value) => {
                    setLanguage(value);
                    handlePreferencesChange();
                  }}
                >
                  <SelectTrigger id="language" className="bg-zinc-800 border-zinc-700 max-w-full">
                    <SelectValue placeholder="Select language" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="en">English</SelectItem>
                    <SelectItem value="es">Spanish</SelectItem>
                    <SelectItem value="fr">French</SelectItem>
                  </SelectContent>
                </Select>
              </div> */}
              
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <label htmlFor="emailOptIn" className="block text-sm font-medium mb-1">Receive email notifications</label>
                  <span className="text-zinc-400 text-sm">Receive updates, tips and special offers</span>
                </div>
                <Switch
                  id="emailOptIn"
                  checked={emailOptIn}
                  onCheckedChange={(checked) => {
                    setEmailOptIn(checked);
                    handlePreferencesChange();
                  }}
                />
              </div>
              
              {preferencesModified && (
                <div className="flex justify-end gap-2 mt-4">
                  <Button
                    onClick={handleCancelPreferences}
                    disabled={isSavingPreferences}
                    className="border-zinc-600 text-zinc-300 hover:bg-zinc-700"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSavePreferences}
                    disabled={isSavingPreferences}
                    className="bg-amber-600 hover:bg-amber-500"
                  >
                    {isSavingPreferences ? (
                      <RefreshCw className="h-5 w-5 animate-spin mr-2" />
                    ) : 'Save Changes'}
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Sign out */}
          <div className="bg-gradient-to-br from-zinc-700 to-zinc-800 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold text-white mb-4">Account session</h2>
            <Button onClick={handleSignOut} className="">
              <LogOut className="h-5 w-5" />
              <span>Sign out</span>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
} 